import axios from 'axios';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

// Add this fingerprint utility function at the top of the file
const getFingerprint = async () => {
  const fp = await FingerprintJS.load();
  const result = await fp.get();
  return result.visitorId;
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add interceptor for attaching token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;

    }
    return config;
  },
  (error) => Promise.reject(error)
);


export const fetchUserProfile = () => api.get('/auth/users/profile');
// export const updateUserDetails = (details) => api.put('/auth/users/details', details);
export const updateUserDetails = async (formData) => {
  try {
    // If profileImage is present, use the profile image upload endpoint
    if (formData.profileImage) {
      const response = await api.post('/auth/users/profile/image', {
        image: formData.profileImage
      });
      return response.data;
    }

    // Otherwise, use the regular update endpoint
    const response = await api.put('/auth/users/details', {
      email: formData.email,
      mobile: formData.mobile,
      gender: formData.gender,
      dob: formData.dob,
      password: formData.password,
      currentPassword: formData.currentPassword,
      country: formData.country || "",
      state: formData.state || "",
      city: formData.city || "",
      postalCode: formData.postalCode || "",
      selectedContents: formData.contentType
    });
    return response.data; // This returns { success: true, user: {...} }
  } catch (error) {
    console.error('Update user details error:', error);
    throw error.response?.data || { message: 'Failed to update user details' };
  }
};

export const fetchInstagramOAuth = (params) => api.get(`/auth/instagram/callback?${encodeURIComponent(params)}`);

// Fetch user insights from authentication service
export const fetchUserInsights = () => api.get('/auth/users/insights');


export const fetchUsermedia = async () => {
  try {
    const response = await api.get('/auth/users/media');
    return response;
  } catch (error) {
    console.error('Media fetch error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
};


// Fetch contract status
export const fetchContractStatus = () => api.get('/auth/users/contract-status');

// Fetch bank details
export const fetchBankDetails = () => api.get('/auth/users/bank-details');

// Fetch pending invitation count for influencer
export const fetchPendingInvitationCount = (email) => api.get(`/company/influencer/invitations/count?email=${encodeURIComponent(email)}`);

// Update bank details (Only if contract status is 'signed' or 'in progress')
export const updateBankDetails = (bankData) => api.post('/auth/users/bank-details', bankData);

// YouTube management functions
export const disconnectYouTube = () => api.post('/auth/youtube/disconnect');
export const refreshYouTubeToken = () => api.post('/auth/youtube/refresh-token');

// Facebook management functions
export const disconnectFacebook = () => api.post('/auth/facebook/disconnect');

// New Affiliate Service APIs
export const affiliateApi = {
  // Check if link exists
  checkExistingLink: async (requestData) => {
    try {
      const response = await api.post('/affiliate/check-link', requestData);
      return response.data;
    } catch (error) {
      console.error('Check link error:', error);
      throw new Error(error.response?.data?.message || 'Failed to check link');
    }
  },

  // Generate new affiliate link
  generateLink: async (requestData) => {
    try {
      const response = await api.post('/affiliate/generate-link', requestData);
      return response.data;
    } catch (error) {
      console.error('Generate link error:', error);
      throw error.response?.data || { error: 'Failed to generate affiliate link' };
    }
  },

  // Get all affiliate links for the user
  getLinks: async () => {
    try {
      const response = await api.get('/affiliate/get-links');
      return response.data.data;
    } catch (error) {
      console.error('Get links error:', error);
      throw error.response?.data || { error: 'Failed to fetch affiliate links' };
    }
  },

  // Get aggregated user statistics
  getUserStats: async () => {
    try {
      const response = await api.get('/affiliate/user-stats');
      return response.data.data;
    } catch (error) {
      console.error('Get user stats error:', error);
      throw error.response?.data || { error: 'Failed to fetch user statistics' };
    }
  },

  // Get redirect URL for a short code
  getRedirectUrl: async (shortCode) => {
    try {
      const response = await api.get(`/affiliate/redirect/${shortCode}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get redirect URL' };
    }
  },

  // Get earnings history
  getEarnings: async () => {
    try {
      const response = await api.get('/affiliate/earnings');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to fetch earnings' };
    }
  },

  // Get estimated earnings for current month
  getEstimatedEarnings: async () => {
    try {
      const response = await api.get('/affiliate/earnings/estimated');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to fetch estimated earnings' };
    }
  },

  // Get payment history
  getPaymentHistory: async () => {
    try {
      const response = await api.get('/affiliate/payments/history');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to fetch payment history' };
    }
  },

  // Track link click and get redirect URL
  trackAndRedirect: async (shortCode) => {
    try {
      // First track the click
      const trackResponse = await api.post(`/affiliate/track-click/${shortCode}`);

      if (trackResponse.data.success) {
        // If tracking successful, return the actual URL to redirect to
        return trackResponse.data.actualLink;
      } else {
        throw new Error('Failed to track click');
      }
    } catch (error) {
      console.error('Track and redirect error:', error);
      // If there's an error, still try to get the redirect URL
      const redirectResponse = await api.get(`/affiliate/redirect/${shortCode}`);
      return redirectResponse.data.actualLink;
    }
  },

  // Handle click tracking and redirection
  handleLinkClick: async (shortCode, fingerprint) => {
    try {
      const response = await api.get(`/affiliate/redirect/${shortCode}`, {
        headers: {
          'x-device-fingerprint': fingerprint
        }
      });
      // Return the full response data instead of just actualLink
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to process link');
    }
  },

  deleteLink: async (linkId) => {
    const response = await api.delete(`/affiliate/delete/${linkId}`);
    return response.data;
  },

  trackPaymentPageVisit: async (shortCode, clickId) => {
    try {
      const fingerprint = await getFingerprint(); // Get fingerprint before making request
      const response = await api.post(
        `/affiliate/track-payment/${shortCode}`,
        { clickId },
        {
          headers: {
            'x-device-fingerprint': fingerprint
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error tracking payment:', error);
      throw error;
    }
  }
};

export const getUserInstagramMedia = async () => {
  try {
    const response = await api.get('/auth/instagram/media');
    return response.data;
  } catch (error) {
    console.error('Error fetching Instagram media:', error);
    throw error.response?.data || { message: 'Failed to fetch Instagram media' };
  }
};


export default api;
